import React, { FC, Suspense, useState } from 'react'
import Modal from '@mui/material/Modal'
import Backdrop from '@mui/material/Backdrop'
import { Shared } from 'ui/shared'
import Fade from '@mui/material/Fade'
import Box from '@mui/material/Box'
import { InputAdornment, TextField, Typography } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { Download, Percent } from 'lucide-react'
import { useDispatch } from 'react-redux'
import { useSelector } from 'react-redux'
import { TState } from 'redux/config/root-types'
import DateConverter from 'utils/helpers/date'
import { Actions_CurrentUser } from 'redux/modules/currentUser/Actions'
import { Actions_Bookings } from 'redux/modules/bookings/Actions'

type Props = {
  isOpen: boolean
  closeModal: () => void
}

export const CancelBookingModal: FC<Props> = (props) => {
  const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    boxShadow: 24,
    borderRadius: '10px',
    maxHeight: 'calc(100vh - 48px)',
    display: 'flex',
  }
  const dispatch = useDispatch()
  const { classes } = useStyles()

  const booking = useSelector((state: TState) => state.bookings.currentBooking)

  const [percent, setPercent] = useState<string>('')

  const onCancelConfirm = () => {
    if (booking?.booking.id) {
      dispatch(
        Actions_Bookings.saga.cancelBooking({
          id: booking?.booking.id,
          percent: Number(percent),
          onSuccess: props.closeModal,
        }),
      )
    }
  }

  const handlePercentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPercent(e.target.value)
  }

  const downloadInvoice = () => {
    if (booking?.invoiceId) {
      dispatch(Actions_CurrentUser.saga.getInvoice({ id: booking.invoiceId }))
    }
  }

  return (
    <Modal
      key={props.isOpen ? 'modal-open' : 'modal-closed'}
      className="modal-portal"
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={props.isOpen}
      onClose={props.closeModal}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          timeout: 500,
        },
      }}
    >
      <Fade in={props.isOpen}>
        <Box sx={style}>
          <Suspense fallback={<Shared.Preloader color={'secondary'} />}></Suspense>
          <Shared.Card style={{ width: 375 }}>
            <div>
              <Typography variant="h6">Cancel booking</Typography>
            </div>

            <div className={classes.col}>
              {booking && booking.invoiceSerial && booking.invoiceDate && (
                <div className={classes.trailerInfo}>
                  <Box>
                    <Typography variant="body1" sx={{ color: '#030712' }}>
                      {booking.invoiceSerial || ''}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#6B7280' }}>
                      {DateConverter.formatDate(booking.invoiceDate) || ''}
                    </Typography>
                  </Box>
                  <Download size="1.25rem" style={{ cursor: 'pointer', color: '#F97316' }} onClick={downloadInvoice} />
                </div>
              )}

              <Typography variant="body2" sx={{ color: '#6B7280', marginTop: '1rem' }}>
                Refund percentage on cancellation
              </Typography>
              <TextField
                type="number"
                value={percent}
                onChange={handlePercentChange}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Percent size="1.25rem" />
                    </InputAdornment>
                  ),
                  inputProps: { min: 0, step: 0.01, max: 100 },
                }}
                variant="standard"
                fullWidth
                margin="normal"
                sx={{ mt: 0 }}
              />
              <Shared.Button
                title={'Confirm'}
                disabled={percent.length === 0}
                onClick={onCancelConfirm}
                style={{ marginTop: '1rem' }}
              />
            </div>
          </Shared.Card>
        </Box>
      </Fade>
    </Modal>
  )
}

const useStyles = makeStyles()((theme) => ({
  row: {
    display: 'flex',
    alignItems: 'center',
  },
  col: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: '1rem',
  },
  trailerInfo: {
    display: 'flex',
    flexDirection: 'row',
    flex: 1,
    border: '1px solid #E5E7EB',
    borderRadius: 4,
    padding: '0.75rem 1rem',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
}))
